import { FC, SetStateAction, useEffect, useState } from 'react';

import {
  DownOutlined,
  SaveOutlined,
  PlusOutlined,
  FontSizeOutlined,
  PieChartOutlined,
  TableOutlined,
  NumberOutlined,
} from '@ant-design/icons';
import {
  Button,
  Dropdown,
  Form,
  Input,
  MenuProps,
  Modal,
  Tooltip,
} from 'antd';
import axios from 'axios';
import moment from 'moment/moment';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  formatPeriod,
  formatPeriodDate,
  getFiltersWithoutSegment,
} from '../../../../../../helpers/utils/functions';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../../../../../helpers/utils/ui';
import AutoReloadButton from '../../../../../../pages/dashboards/components/AutoReloadButton';
import { BlockVisible } from '../../../../types/dashboard';
import { axiosConfigAuth } from '../../../../api/middleware';
import { API_UN_REPORT_EXEL } from '../../../../api/urls';
import { Roles } from '../../../../constants/user';
import { useActions } from '../../../../hooks/useActions';
import { useTypedSelector } from '../../../../hooks/useTypedSelector';

type Props = {
  setAddBlockVisible: React.Dispatch<
    SetStateAction<BlockVisible>
  >;
  isCompact?: boolean; // Компактный режим с иконками
  /** Внешняя функция сохранения (из useSaveDashboardShortcut) */
  onSave?: () => void;
  /** Открыть модалку "Сохранить как" (управляется из Dashboards через Cmd+S) */
  saveAsModalVisible?: boolean;
  /** Закрыть модалку "Сохранить как" */
  onCloseSaveAsModal?: () => void;
  /** Callback для обновления данных дашборда (без перезагрузки страницы) */
  onRefresh?: () => void;
};
const UniversalReportTitle: FC<Props> = ({ setAddBlockVisible, isCompact = false, onSave, saveAsModalVisible, onCloseSaveAsModal, onRefresh }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { role } = useTypedSelector((state) => state.currentUser);
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { savedDashboards, currentDashboard, isLoadingReports } = useTypedSelector(
    (state) => state.reports
  );
  const period = useTypedSelector((state) => state.period);
  const { dashboards } = useTypedSelector((state) => state.dashboard);
  const detailsDashboard = useTypedSelector(
    (state) => state.details.detailsDashboard
  );
  const { filters, currentSegment } = useTypedSelector((state) => state.filter);
  const { attributes, attributesDashboard } = useTypedSelector(
    (state) => state.attributes
  );
  const {
    metrics,
    groupings,
  } = useTypedSelector((state) => state.table);
  const { isCompare } = useTypedSelector((state) => state.meta);

  const {
    postReport,
    putReport,
  } = useActions();
  const [ isModalVisible, setIsModalVisible ] = useState(false);
  const [ isModalCreateVisible, setIsModalCreateVisible ] = useState(false);
  const [ isModalXLSXVisible, setIsModalXLSXVisible ] = useState(false);
  const [ reportName, setReportName ] = useState('');
  const [ excelName, setExcelName ] = useState('');
  const [ isDisableSave, setIsDisableSave ] = useState(false);

  const reportParam = new URLSearchParams(location.search).get('report_id');

  useEffect(() => {
    if (savedDashboards.find((dashboard) => dashboard?.hash === reportParam)?.access_id <= Roles[role]) {
      setIsDisableSave(false);
    } else {
      setIsDisableSave(true);
    }
  }, [ reportParam, savedDashboards ]);

  const save = () => {
    // Если передана внешняя функция сохранения — используем её
    // (включает zoom, sortTable, columnWidth, columnsWidth)
    if (onSave) {
      onSave();
      return;
    }

    let values: any = {
      ...currentDashboard.values,
      period: {
        start: formatPeriodDate(period.start, period.withTime),
        end: formatPeriodDate(period.end, period.withTime),
        interval: period.interval || period.select,
        select: period.select || period.interval,
        days: null,
      },
      attributesDashboard,
      detailsDashboard,
      filters,
      segment: null,
      blocks: dashboards,
    };

    if (Object.keys(currentSegment || {}).length > 0) {
      values.segment = currentSegment;
      values.filters = getFiltersWithoutSegment(
        filters,
        currentSegment?.values?.filters
      );
    } else {
      values.segment = {};
      values.filters = filters;
    }

    if (selectedSchool?.id) {
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      putReport(selectedSchool.id, { id: currentDashboard.id, values });
    }
  };

  const saveAs = () => {
    if (selectedSchool?.id) {
      let values: any = {
        name: reportName,
        period: {
          start: formatPeriodDate(period.start, period.withTime),
          end: formatPeriodDate(period.end, period.withTime),
          interval: period.interval || period.select,
          select: period.select || period.interval,
        },
        attributesDashboard,
        detailsDashboard,
        filters,
        segment: null,
        blocks: dashboards,
      };

      if (Object.keys(currentSegment || {}).length > 0) {
        values.segment = currentSegment;
        values.filters = getFiltersWithoutSegment(
          filters,
          currentSegment?.values?.filters
        );
      } else {
        let _filters = filters;
        if (!Array.isArray(filters)) {
          _filters = [];
        }
        values.filters = _filters;
      }
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      const onSuccess = (report_id: string) => navigate(window.location.pathname + '?report_id=' + report_id);
      postReport(selectedSchool.id, { values, onSuccess });
      setIsModalVisible(false);
      onCloseSaveAsModal?.();
      setReportName('');
    }
  };

  const create = () => {
    if (selectedSchool?.id) {
      let values: any = {
        name: reportName,
        period: {
          start: formatPeriodDate(period.start, period.withTime),
          end: formatPeriodDate(period.end, period.withTime),
          interval: period.interval || period.select,
          select: period.select || period.interval,
        },
        attributesDashboard,
        detailsDashboard,
        filters,
        segment: null,
        blocks: dashboards,
      };
      if (Object.keys(currentSegment || {}).length > 0) {
        values.segment = currentSegment;
        values.filters = getFiltersWithoutSegment(
          filters,
          currentSegment?.values?.filters
        );
      } else {
        let _filters = filters;
        if (!Array.isArray(filters)) {
          _filters = [];
        }
        values.filters = _filters;
      }
      if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
        values.period.days = period.days;
      }
      const onSuccess = (report_id: string) => navigate(window.location.pathname + '?report_id=' + report_id);
      postReport(selectedSchool.id, { values, onSuccess });
      setIsModalCreateVisible(false);
      setReportName('');
    }
  };

  const saveXSLX = () => {
    const format = 'YYYY-MM-DD';
    const _period = {
      ...formatPeriod({ start: period.start, end: period.end }, format),
    };
    const data = {
      name: excelName,
      period: _period,
      filters: attributes,
      groupings,
      metrics,
    };

    axios
      .post(API_UN_REPORT_EXEL, data, axiosConfigAuth())
      .then(() => {
        showSuccessMessage('Сохранено');
      })
      .catch(() => {
        showErrorMessage('Ошибка при сохранении');
      });

    setIsModalXLSXVisible(false);
    setExcelName('');
  };


  const savingItemsDropDown: MenuProps['items'] = [
    {
      key: '1',
      label: Object.keys(currentDashboard).length > 0 && !isDisableSave && (
        <span onClick={save}>Сохранить изменения</span>
      ),
    },
    {
      key: '2',
      label: <span onClick={() => setIsModalVisible(true)}>Сохранить как новый дашборд</span>,
    },
  ];

  const menuItems = [
        {
          key: '1',
          icon: <FontSizeOutlined />,
          label: 'Текст',
          onClick: () =>
            setAddBlockVisible({ visible: true, blockType: 'heading' }),
        },
        {
          key: '2',
          icon: <PieChartOutlined />,
          label: 'Диаграмма',
          onClick: () => setAddBlockVisible({ visible: true, blockType: 'chart' }),
        },
        {
          key: '3',
          icon: <TableOutlined />,
          label: 'Таблица',
          onClick: () => setAddBlockVisible({ visible: true, blockType: 'table' }),
        },
        {
          key: '4',
          icon: <NumberOutlined />,
          label: 'Число',
          onClick: () => setAddBlockVisible({ visible: true, blockType: 'number' }),
        },
  ];

  // Компактный режим с иконками
  if (isCompact) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {!isCompare && dashboards?.length > 0 && (
          <>
            <AutoReloadButton isCompact={true} onRefresh={onRefresh} />
            <Dropdown
              disabled={isCompare}
              menu={{ items: savingItemsDropDown }}
              trigger={['click']}
            >
              <Tooltip title="Сохранить">
                <Button
                  data-analytics-id="dashboard-save"
                  type="text"
                  icon={<SaveOutlined />}
                  disabled={isCompare}
                  style={{
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                  }}
                />
              </Tooltip>
            </Dropdown>
          </>
        )}
        <Dropdown trigger={['click']} menu={{ items: menuItems }}>
          <Tooltip title="Добавить блок">
            <Button
              data-analytics-id="dashboard-add-block"
              type="primary"
              icon={<PlusOutlined />}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fe3f30',
                border: 'none',
                borderRadius: '6px',
              }}
            />
          </Tooltip>
        </Dropdown>

        {/* Модальные окна */}
        <Modal
          title="Сохранить дашборд как"
          open={isModalVisible || saveAsModalVisible}
          onOk={() =>
            reportName
              ? saveAs()
              : showErrorMessage('Введите название дашборда')
          }
          onCancel={() => { setIsModalVisible(false); onCloseSaveAsModal?.(); }}
          okText="Сохранить"
        >
          <Form>
            <Form.Item
              name="reportName"
              label="Название дашборда"
              rules={[
                { required: true, message: 'Введите название дашборда' },
                { min: 1, message: 'Введите название дашборда' },
              ]}
            >
              <Input
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                size="large"
                placeholder="Введите название дашборда"
                allowClear
              />
            </Form.Item>
          </Form>
        </Modal>
        <Modal
          title="Создать дашборд"
          open={isModalCreateVisible}
          onOk={create}
          onCancel={() => setIsModalCreateVisible(false)}
          okText="Сохранить"
        >
          <Input
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            size="large"
            placeholder="Введите название дашборда"
            allowClear
          />
        </Modal>
        <Modal
          title="Скачать XLSX таблицы"
          open={isModalXLSXVisible}
          onOk={saveXSLX}
          onCancel={() => setIsModalXLSXVisible(false)}
          okText="Сохранить"
        >
          <Input
            value={excelName}
            onChange={(e) => setExcelName(e.target.value)}
            size="large"
            placeholder="Введите название файла"
            allowClear
          />
        </Modal>
      </div>
    );
  }

  return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '95%',
              marginTop: '10px',
              marginLeft: '100px',
              zIndex: 2
            }}
          >

         
            {!isCompare && dashboards?.length > 0 && (
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <span data-help-id="dashboard-auto-refresh"><AutoReloadButton onRefresh={onRefresh} /></span>
                <Dropdown
                  overlayStyle={{ left: '1490px' }}
                  disabled={isCompare}
                  menu={{ items: savingItemsDropDown }}
                  trigger={[ 'click' ]}
                >
                  <Button data-help-id="dashboard-save" disabled={isCompare}
                  style={{
                    height: '45px',
                    borderRadius: '5px',
                  }} >
                    Сохранить
                  </Button>
                </Dropdown>
              </div>
            )}
            <Dropdown trigger={[ 'click' ]} menu={{ items: menuItems }}>
              <Button
                data-help-id="dashboard-add-block"
                type="primary"
                style={{
                  backgroundColor: '#fe3f30',
                  color: 'white',
                  width: '170px',
                  height: '45px',
                  borderRadius: '5px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Добавить блок <DownOutlined />
              </Button>
            </Dropdown>
          </div>
          <Modal
            title="Сохранить дашборд как"
            open={isModalVisible || saveAsModalVisible}
            onOk={() =>
              reportName
                ? saveAs()
                : showErrorMessage('Введите название дашборда')
            }
            onCancel={() => { setIsModalVisible(false); onCloseSaveAsModal?.(); }}
            okText="Сохранить"
          >
            <Form>
              <Form.Item
                name="reportName"
                label="Название дашборда"
                rules={[
                  { required: true, message: 'Введите название дашборда' },
                  { min: 1, message: 'Введите название дашборда' },
                ]}
              >
                <Input
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  size="large"
                  placeholder="Введите название дашборда"
                  allowClear
                />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title="Создать дашборд"
            open={isModalCreateVisible}
            onOk={create}
            onCancel={() => setIsModalCreateVisible(false)}
            okText="Сохранить"
          >
            <Input
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              size="large"
              placeholder="Введите название дашборда"
              allowClear
            />
          </Modal>
          <Modal
            title="Скачать XLSX таблицы"
            open={isModalXLSXVisible}
            onOk={saveXSLX}
            onCancel={() => setIsModalXLSXVisible(false)}
            okText="Сохранить"
          >
            <Input
              value={excelName}
              onChange={(e) => setExcelName(e.target.value)}
              size="large"
              placeholder="Введите название файла"
              allowClear
            />
          </Modal>
        </div>
  );
};

export default UniversalReportTitle;
